import React, { useContext, useState } from 'react';
import useForm from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import * as yup from 'yup';

import { InputText } from 'primereact/inputtext';
import { Field } from '@components/framework/formfield/Field.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon.tsx';
import SvgRoute from '@icons/route.svg?react';
import SvgTruck from '@icons/truck.svg?react';
import SvgInfoCircle from '@icons/info.svg?react';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { SchenkerAdditionalInfoInDTO, SchenkerAddonsTypeEnum } from '@api/ringil-api.v2.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';

interface ComponentProps {
}

interface SidebarData {
  quoteDemandId: string;
  expeditionApplicationId: string;
  carrierName: string;
  data?: SchenkerAdditionalInfoInDTO;
  onContinue: () => void;
  isEditable: boolean;
  isDeletable: boolean;
  reloadData?: () => void;
  schenkerAddonsType?: SchenkerAddonsTypeEnum[];
}

export const useSchenkerQuoteAdditionalSidebar = () => {
  const [sidebarData, setSidebarData] = useState<SidebarData>(undefined);

  const SchenkerQuoteAdditionalSidebar: React.FC<ComponentProps> = () => {
    const { saveSchenkerAdditionalData, deleteSchenkerAdditionalData } = useContext(ApiContext);
    const { tr } = useTranslationLgs();

    const showDeclarantInfo = sidebarData?.schenkerAddonsType?.includes(SchenkerAddonsTypeEnum.CUSTOMS_SHIPMENT_OTHERS);
    const showValueOfGoods =
      sidebarData?.schenkerAddonsType?.includes(SchenkerAddonsTypeEnum.CUSTOMS_SHIPMENT_OTHERS) ||
      sidebarData?.schenkerAddonsType?.includes(SchenkerAddonsTypeEnum.CUSTOMS_SHIPMENT_NOT_REQUIRED);

    const formSchema = yup.object().shape({
      ...(showDeclarantInfo
        ? {
            declarantInfo: yup.object().shape({
              companyName: yup.string().required(tr('Form.companyNameRequired', 'Company name is required')),
              street: yup.string().required(tr('Form.streetRequired', 'Street is required')),
              city: yup.string().required(tr('Form.cityRequired', 'City is required')),
              postalCode: yup.string().required(tr('Form.postalCodeRequired', 'Postal code is required')),
              countryCode: yup.string().required(tr('Form.countryCodeRequired', 'Country code is required')),
              phone: yup.string().required(tr('Form.phoneRequired', 'Phone is required')),
              email: yup
                .string()
                .required(tr('Form.emailRequired', 'Email is required'))
                .email(tr('Form.emailInvalid', 'Please enter a valid email address')),
            }),
          }
        : {}),
      ...(showValueOfGoods
        ? {
            valueOfGoods: yup.object().shape({
              price: yup.number().required(tr('Form.priceRequired', 'Price is required')).typeError(tr('Form.priceMustBeNumber', 'Price must be a number')),
              currency: yup.string().required(tr('Form.currencyRequired', 'Currency is required')),
            }),
          }
        : {}),
    });

    const initialValues: SchenkerAdditionalInfoInDTO = {
      declarantInfo: {
        companyName: '',
        street: '',
        postalCode: '',
        city: '',
        countryCode: '',
        phone: '',
        email: '',
      },
      valueOfGoods: {
        price: null,
        currency: 'CZK',
      },
    };

    const form = useForm<SchenkerAdditionalInfoInDTO>(
      formSchema,
      sidebarData?.data || initialValues,
      formData => {
        saveSchenkerAdditionalData(sidebarData?.quoteDemandId, formData, () => {
          sidebarData?.reloadData();
          setSidebarData(null);
          sidebarData.onContinue();
        });
      },
      false,
      false,
    );

    if (isNullOrUndefined(sidebarData)) return null;

    return (
      <RingilSidebar
        visible={isNotNullOrUndefined(sidebarData)}
        heading={tr(`SchenkerQuoteAdditionalSidebar.title`, `Add additional Schenker quote information`)}
        onBack={() => setSidebarData(null)}
        continueLabel={tr(`Form.continueLabel`, `Continue`)}
        onContinue={() => form.validateAndSend()}
        onDeleteForm={sidebarData.isDeletable ? () => {
          form.setValues?.(initialValues);
          form.clear();
          if (!sidebarData?.isEditable) {
            deleteSchenkerAdditionalData(sidebarData?.quoteDemandId, () => {
              setSidebarData(null);
              sidebarData?.reloadData();
            });
          }
          }
          : null
        }
        showSaveButton={sidebarData?.isEditable}
      >
        <div className="flex flex-col gap-4">
          <VerticalFieldAndValueWithIcon
            label={tr('Form.forExpedition', 'For expedition')}
            valueText={sidebarData.expeditionApplicationId}
            icon={<SvgRoute />}
          />
          <VerticalFieldAndValueWithIcon
            label={tr('Form.selectedCarrier', 'Selected carrier')}
            valueText={sidebarData.carrierName}
            icon={<SvgTruck />}
          />
          <COInlineMessage
            variant={'info'}
            icon={<SvgInfoCircle className="text-content-tertiary" />}
          >
            {tr('QuoteAdditionalInfo.additionalInfoRequiredMessage', 'The carrier requires this information for the transport of the cargo')}
          </COInlineMessage>

          {showDeclarantInfo && (
            <div className="flex flex-col gap-2">
              <COHeadline variant={'h4'} title={tr('SchenkerQuoteAdditionalSidebar.declarantInfoHeadline', 'Declarant information')} />
              <Field
                label={tr('Form.companyName', 'Company name')}
                required={!form.values.declarantInfo?.companyName}
                errorMessage={findErrorMessage(form.validationErrors, 'declarantInfo.companyName', tr)}
                disabled={!sidebarData?.isEditable}
              >
                <InputText
                  value={form.values.declarantInfo?.companyName}
                  onChange={e => form.setFieldValue('declarantInfo.companyName', e.target.value)}
                />
              </Field>
              <Field
                label={tr('Form.street', 'Street')}
                required={!form.values.declarantInfo?.street}
                errorMessage={findErrorMessage(form.validationErrors, 'declarantInfo.street', tr)}
                disabled={!sidebarData?.isEditable}
              >
                <InputText
                  value={form.values.declarantInfo?.street}
                  onChange={e => form.setFieldValue('declarantInfo.street', e.target.value)}
                />
              </Field>
              <Field
                label={tr('Form.zipCode', 'Zip code')}
                required={!form.values.declarantInfo?.postalCode}
                errorMessage={findErrorMessage(form.validationErrors, 'declarantInfo.postalCode', tr)}
                disabled={!sidebarData?.isEditable}
              >
                <InputText
                  value={form.values.declarantInfo?.postalCode}
                  onChange={e => form.setFieldValue('declarantInfo.postalCode', e.target.value)}
                />
              </Field>
              <Field
                label={tr('Form.city', 'City')}
                required={!form.values.declarantInfo?.city}
                errorMessage={findErrorMessage(form.validationErrors, 'declarantInfo.city', tr)}
                disabled={!sidebarData?.isEditable}
              >
                <InputText
                  value={form.values.declarantInfo?.city}
                  onChange={e => form.setFieldValue('declarantInfo.city', e.target.value)}
                />
              </Field>
              <Field
                label={tr('Form.countryCode', 'Country code')}
                required={!form.values.declarantInfo?.countryCode}
                errorMessage={findErrorMessage(form.validationErrors, 'declarantInfo.countryCode', tr)}
                disabled={!sidebarData?.isEditable}
              >
                <InputCountry
                  value={form.values.declarantInfo?.countryCode}
                  onChange={v => form.setFieldValue('declarantInfo.countryCode', v)}
                />
              </Field>
              <Field
                label={tr('Form.phone', 'Phone')}
                required={!form.values.declarantInfo?.phone}
                errorMessage={findErrorMessage(form.validationErrors, 'declarantInfo.phone', tr)}
                disabled={!sidebarData?.isEditable}
              >
                <InputText
                  value={form.values.declarantInfo?.phone}
                  onChange={e => form.setFieldValue('declarantInfo.phone', e.target.value)}
                />
              </Field>
              <Field
                label={tr('Form.email', 'Email')}
                required={!form.values.declarantInfo?.email}
                errorMessage={findErrorMessage(form.validationErrors, 'declarantInfo.email', tr)}
                disabled={!sidebarData?.isEditable}
              >
                <InputText
                  value={form.values.declarantInfo?.email}
                  onChange={e => form.setFieldValue('declarantInfo.email', e.target.value)}
                />
              </Field>
            </div>
          )}

          {showValueOfGoods && (
            <div className="flex flex-col gap-2">
              <COHeadline variant={'h4'} title={tr('SchenkerQuoteAdditionalSidebar.valueOfGoodsHeadline', 'Value of goods')} />
              <Field
                label={tr('Form.valueOfGoods', 'Value of goods')}
                required={!form.values.valueOfGoods?.price}
                errorMessage={findErrorMessage(form.validationErrors, 'valueOfGoods.price', tr)}
                disabled={!sidebarData?.isEditable}
                skipCurrencyCssMode
              >
                <InputPriceWithCurrency
                  value={form.values.valueOfGoods?.price}
                  currency={form.values.valueOfGoods?.currency}
                  changePrice={v => form.setFieldValue('valueOfGoods.price', v)}
                  changeCurrency={v => form.setFieldValue('valueOfGoods.currency', v)}
                  disabled={!sidebarData?.isEditable}
                />
              </Field>
            </div>
          )}
        </div>
      </RingilSidebar>
    );
  };

  return { SchenkerQuoteAdditionalSidebar, setSidebarData };
};
